import { useState } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import api from "api/api";
import loaderSignal from "signals/Loader.signal";
import alertSignal from "signals/Alert.signal";
import currencyOptions from "../Constant/currencyOptions";
import {
  exportChartData,
  exportChartDataXlsx,
} from "./AdhocReporting.helpers";

/**
 * Aug 10 Gustavo: full 111-col extract is a Platform option, not Select Report.
 * Sentinel value (not a DB platform id).
 */
const ALL_LOAN_DATA_PLATFORM = 'all-loan-data';
const ALL_LOAN_DATA_LABEL = 'All Loan Data';

type ReportRow = {
  calcapId: string;
  disbursementDate: string;
  programName: string;
  platformName: string;
  amount: number;
};

type DisbursementRecord = {
  premiumDate?: string | Date | null;
  dueDate?: string | Date | null;
  createdDate?: string | Date | null;
};

/** Grid Disbursement Date column only. From/To is Application.createdDate (Teddy Sep 28). */
const getDisbursementCreatedDate = (d: DisbursementRecord): Date | null => {
  if (!d.createdDate) return null;
  return new Date(d.createdDate);
};

const formatReportDate = (date: Date | null): string =>
  date
    ? date.toLocaleDateString('en-US', { timeZone: 'UTC' })
    : 'N/A';

const utcDayStart = (yyyyMmDd: string) => `${yyyyMmDd}T00:00:00.000Z`;
const utcDayEnd = (yyyyMmDd: string) => `${yyyyMmDd}T23:59:59.999Z`;

const AdhocReporting = () => {
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [disbursementDateFrom, setDisbursementDateFrom] = useState<string>('');
  const [disbursementDateTo, setDisbursementDateTo] = useState<string>('');
  const [platform, setPlatform] = useState<string>('');
  const [program, setProgram] = useState<string>('');
  const [results, setResults] = useState<ReportRow[]>([]);
  const [platforms, setPlatforms] = useState<{ id: string; name: string }[]>([]);
  const [programs, setPrograms] = useState<{ id: string; name: string }[]>([]);
  /** All Loan Data: count + export only after Generate (no grid). */
  const [allLoanDataReady, setAllLoanDataReady] = useState(false);
  const [allLoanDataCount, setAllLoanDataCount] = useState<number | null>(null);

  const isAllLoanData = platform === ALL_LOAN_DATA_PLATFORM;

  /**
   * Date From/To = Application.createdDate (Teddy Record Creation Date).
   * Optional Disbursement Date From/To = Fund.fundDate on that fund line (B / L / SSBCI / IC).
   * Leave Disbursement Date blank to keep pending / no-fund rows.
   */
  const buildAdhocWhere = (rangeStart: string, rangeEnd: string) => {
    const createdDateFilter = {
      createdDate: {
        gte: rangeStart,
        lte: rangeEnd,
      },
    };
    const hasDisbursementRange = Boolean(
      disbursementDateFrom && disbursementDateTo
    );
    const fundDateFilter = hasDisbursementRange
      ? {
          funds: {
            some: {
              fundDate: {
                gte: utcDayStart(disbursementDateFrom),
                lte: utcDayEnd(disbursementDateTo),
              },
            },
          },
        }
      : null;

    const dateFilters = fundDateFilter
      ? { AND: [createdDateFilter, fundDateFilter] }
      : createdDateFilter;

    if (isAllLoanData) {
      return dateFilters;
    }

    return {
      ...(program && { fundingProgramId: Number(program) }),
      ...(platform && !program && {
        fundingProgram: {
          is: {
            platformId: Number(platform),
          },
        },
      }),
      ...dateFilters,
    };
  };

  const resetReportOutput = () => {
    setResults([]);
    setAllLoanDataReady(false);
    setAllLoanDataCount(null);
  };

  const fetchResults = async () => {
    if (!dateFrom || !dateTo) {
      return alertSignal.update({
        type: 'notification',
        message: 'Please select both Date From and Date To',
      });
    }

    if (
      (disbursementDateFrom && !disbursementDateTo) ||
      (!disbursementDateFrom && disbursementDateTo)
    ) {
      return alertSignal.update({
        type: 'notification',
        message: 'Please select both Disbursement Date From and Disbursement Date To',
      });
    }

    const rangeStart = new Date(dateFrom).toISOString();
    const rangeEnd = new Date(dateTo).toISOString();
    const where = buildAdhocWhere(rangeStart, rangeEnd);

    // All Loan Data: count only — no grid (Gustavo Aug 10).
    if (isAllLoanData) {
      setResults([]);
      loaderSignal.update({ isContentLoading: true, message: 'Generating report...' });
      try {
        const countResponse = await api.get({
          path: '/v2/applications',
          options: {
            where,
            getCount: true,
            take: 1,
            skip: 0,
          },
        });
        setAllLoanDataCount(Number(countResponse?.count) || 0);
        setAllLoanDataReady(true);
      } catch (error) {
        setAllLoanDataReady(false);
        setAllLoanDataCount(null);
        alertSignal.update({
          type: 'notification',
          error,
          // @ts-ignore
          message: error.message || 'Failed to fetch report data',
        });
      } finally {
        loaderSignal.reset();
      }
      return;
    }

    setAllLoanDataReady(false);
    setAllLoanDataCount(null);

    loaderSignal.update({ isContentLoading: true, message: 'Generating report...' });
    try {
      const countResponse = await api.get({
        path: '/v2/applications',
        options: {
          where,
          getCount: true,
          take: 1,
          skip: 0,
        },
      });

      const totalCount = Number(countResponse?.count) || 0;
      const applications =
        totalCount === 0
          ? []
          : await api.get({
              path: '/v2/applications',
              options: {
                where,
                take: totalCount,
                skip: 0,
              },
            });

      const applicationRows = Array.isArray(applications) ? applications : [];

      const rows: ReportRow[] = applicationRows.map((app: any) => {
        const matchingDisbursement = (app.disbursements || [])[0] as
          | DisbursementRecord
          | undefined;
        const effectiveDate = matchingDisbursement
          ? getDisbursementCreatedDate(matchingDisbursement)
          : null;

        return {
          calcapId: app.calcapLoanNumber || 'N/A',
          disbursementDate: formatReportDate(effectiveDate),
          programName: app.fundingProgram?.name || 'N/A',
          platformName: app.fundingProgram?.platform?.name || 'N/A',
          amount: app.approvedAmount || 0,
        };
      });

      setResults(rows);
    } catch (error) {
      alertSignal.update({
        type: 'notification',
        error,
        // @ts-ignore
        message: error.message || 'Failed to fetch report data',
      });
    } finally {
      loaderSignal.reset();
    }
  };

  const loadPlatforms = async () => {
    try {
      const platformsData = await api.get({
        path: '/platforms',
        options: {
          select: {
            id: true,
            name: true,
          },
        },
      });
      setPlatforms(platformsData);
    } catch (error) {
      console.error('Failed to load platforms:', error);
    }
  };

  const loadPrograms = async (platformId?: string) => {
    try {
      const programsData = await api.get({
        path: '/fundingPrograms',
        options: {
          where: platformId ? { platformId: Number(platformId) } : {},
          select: {
            id: true,
            name: true,
          },
        },
      });
      setPrograms(programsData);
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const exportAllLoanData = async () => {
    if (!dateFrom || !dateTo) {
      return alertSignal.update({
        type: 'notification',
        message: 'Please select both Date From and Date To',
      });
    }

    if (
      (disbursementDateFrom && !disbursementDateTo) ||
      (!disbursementDateFrom && disbursementDateTo)
    ) {
      return alertSignal.update({
        type: 'notification',
        message: 'Please select both Disbursement Date From and Disbursement Date To',
      });
    }

    const rangeStart = new Date(dateFrom).toISOString();
    const rangeEnd = new Date(dateTo).toISOString();
    const where = buildAdhocWhere(rangeStart, rangeEnd);
    const hasDisbursementRange = Boolean(
      disbursementDateFrom && disbursementDateTo
    );

    loaderSignal.update({ isContentLoading: true, message: 'Exporting All Loan Data...' });
    try {
      await api.get({
        path: '/v2/applications/adhoc-full-dataset/download',
        options: {
          where,
          rangeStart,
          rangeEnd,
          ...(hasDisbursementRange && {
            disbursementRangeStart: utcDayStart(disbursementDateFrom),
            disbursementRangeEnd: utcDayEnd(disbursementDateTo),
          }),
        },
        isFileDownload: true,
      });
    } catch (error) {
      alertSignal.update({
        type: 'notification',
        error,
        // @ts-ignore
        message: error.message || 'Failed to export All Loan Data',
      });
    } finally {
      loaderSignal.reset();
    }
  };

  const calculateTotal = () => results.reduce((sum, row) => sum + row.amount, 0);

  const getExportRows = () =>
    results.map((row) => ({
      'CalCAP ID': row.calcapId,
      'Disbursement Date': row.disbursementDate,
      Program: row.programName,
      Platform: row.platformName,
      Amount: Number(Number(row.amount || 0).toFixed(2)),
    }));

  const getAppliedFiltersSummary = () => {
    const platformName = isAllLoanData
      ? ALL_LOAN_DATA_LABEL
      : platforms.find((p) => String(p.id) === String(platform))?.name ||
        (platform ? platform : 'All Platforms');
    const programName = isAllLoanData
      ? 'All Programs'
      : programs.find((p) => String(p.id) === String(program))?.name ||
        (program ? program : 'All Programs');

    return [
      `Date From: ${dateFrom || 'N/A'}`,
      `Date To: ${dateTo || 'N/A'}`,
      `Disbursement Date From: ${disbursementDateFrom || 'N/A'}`,
      `Disbursement Date To: ${disbursementDateTo || 'N/A'}`,
      `Platform: ${platformName}`,
      `Program: ${programName}`,
    ];
  };

  return (
    <Card className="rounded-0">
      <Card.Body>
        <h3 className="text-center">Reporting</h3>
        <hr />
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Date From</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Date To</Form.Label>
              <Form.Control
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Disbursement Date From</Form.Label>
              <Form.Control
                type="date"
                value={disbursementDateFrom}
                onChange={(e) => setDisbursementDateFrom(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Disbursement Date To</Form.Label>
              <Form.Control
                type="date"
                value={disbursementDateTo}
                onChange={(e) => setDisbursementDateTo(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Platform</Form.Label>
              <Form.Control
                as="select"
                value={platform}
                onChange={(e) => {
                  const selectedPlatform = e.target.value;
                  setPlatform(selectedPlatform);
                  setProgram('');
                  setPrograms([]);
                  resetReportOutput();
                  if (
                    selectedPlatform &&
                    selectedPlatform !== ALL_LOAN_DATA_PLATFORM
                  ) {
                    loadPrograms(selectedPlatform);
                  }
                }}
                onFocus={() => {
                  if (platforms.length === 0) {
                    loadPlatforms();
                  }
                }}
              >
                <option value="">All Platforms</option>
                <option value={ALL_LOAN_DATA_PLATFORM}>{ALL_LOAN_DATA_LABEL}</option>
                {platforms.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Program</Form.Label>
              <Form.Control
                as="select"
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                disabled={!platform || isAllLoanData}
                onFocus={() => {
                  if (
                    platform &&
                    !isAllLoanData &&
                    programs.length === 0
                  ) {
                    loadPrograms(platform);
                  }
                }}
              >
                <option value="">All Programs</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Button
              className="rounded"
              onClick={fetchResults}
              disabled={loaderSignal.value.isContentLoading}
            >
              Generate Report
            </Button>
          </Col>
        </Row>
        <hr />
        {!isAllLoanData && results.length > 0 && (
          <>
            <Row className="mb-3">
              <Col className="d-flex justify-content-end">
                <Button
                  className="rounded"
                  variant="outline-secondary"
                  onClick={() => exportChartData(getExportRows() as Record<string, string>[])}
                >
                  Export CSV
                </Button>
                <Button
                  className="rounded ml-8"
                  variant="outline-secondary"
                  onClick={() =>
                    exportChartDataXlsx(getExportRows(), {
                      filters: getAppliedFiltersSummary(),
                      title: 'Ad hoc Report',
                    })
                  }
                >
                  Export Excel
                </Button>
              </Col>
            </Row>
            <Row>
              <Col>
                <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th>CalCAP ID</th>
                      <th>Disbursement Date</th>
                      <th>Program</th>
                      <th>Platform</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, index) => (
                      <tr key={index}>
                        <td>{row.calcapId}</td>
                        <td>{row.disbursementDate}</td>
                        <td>{row.programName}</td>
                        <td>{row.platformName}</td>
                        <td>
                          {row.amount.toLocaleString('en-US', currencyOptions.currencyUS)}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-weight-bold">
                      <td colSpan={4} className="text-right">
                        Total
                      </td>
                      <td>
                        {calculateTotal().toLocaleString('en-US', currencyOptions.currencyUS)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          </>
        )}
        {isAllLoanData && allLoanDataReady && (
          <Row>
            <Col>
              <p className="mb-16">
                {`Records found: ${allLoanDataCount ?? 0}. DataSet too large to display, please proceed with Exporting the Report.`}
              </p>
              <Button
                className="rounded"
                variant="outline-secondary"
                onClick={exportAllLoanData}
                disabled={loaderSignal.value.isContentLoading}
              >
                Export to CSV
              </Button>
            </Col>
          </Row>
        )}
        {!isAllLoanData && results.length === 0 && (
          <Row>
            <Col>
              <p className="text-center text-muted">No results. Select filters and click Generate Report.</p>
            </Col>
          </Row>
        )}
        {isAllLoanData && !allLoanDataReady && (
          <Row>
            <Col>
              <p className="text-center text-muted">
                No results. Select filters and click Generate Report.
              </p>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export default AdhocReporting;
